/**
 * @module cercaliagl/Feature
 */

import WKT from 'wellknown';
import BaseFeature from './BaseFeature';
import Bounds from './Bounds';
import MapControls from './control/MapControls';
import GeometryType from './GeometryType';
import { getClosestLonLatOfFeature } from './util';

/**
 * @typedef {Object} FeatureOptions
 * @property {string} [id] Feature id
 * @property {boolean} [editable] Feature editable using mouse. Default `false`.
 * @property {string} [fillColor] Feature fill color. Default value `#009BFF`.
 * @property {number} [fillOpacity] Feature fill opacity. Default value `0.2`.
 * @property {number} [strokeOpacity] Feature stroke opacity. Default value `1`.
 * @property {string} [strokeColor] Feature stroke color. Default value `#2196F3`.
 * @property {number} [strokeWidth] Feature stroke width. Default value `7`.
 * @property {boolean} [outline] Enable outline feature property. If no outline required, specify null. Default value `true`.
 * @property {string} [outlineColor] Feature outline color. Default value `#040252`.
 * @property {number} [outlineOpacity] Feature outline opacity. Default value `1`.
 * @property {number} [outlineWidth] Feature outline width. Default value `9`.
 * @property {boolean} [visible] Feature visible or not. Default `true`.
 * @property {string} [encodedGoogleLineString] Linestring wkt encoded on google polyline .
 * @property {string} [encodedGooglePolygon] Polygon wkt encoded on google polyline .
 * @property {Array.<string>} [encodedGoogleMultiPolygon] Array of polygon wkts encoded on google polyline .
 * @property {boolean} [visible] Feature visible or not. Default `true`.
 * @property {function(this:Feature): void} [onClick] Function to execute on marker click.
 * @property {function(this:Feature): void} [onDoubleClick] Function to execute on marker double click.
 * @property {function(this:Feature): void} [onMouseOut] Function to execute on Mouse Out.
 * @property {function(this:Feature): void} [onMouseOver] Function to execute on Mouse Over.
 * @property {function(this:Feature): void} [onMouseMove] Function to execute on Mouse Over.
 * @property {function(this:Feature): void} [onRightClick] Function to execute on marker right click.
 * @property {function(this:Feature): void} [onModifyStart] Function to execute on marker right click.
 * @property {function(this:Feature): void} [onModifyEnd]  Function to execute on modify end. When feature is `editable`.
 * @property {string} [wkt] WKT String with shape geometry to be painted. Mandatory to use that or `geometry` property. More info: https://es.wikipedia.org/wiki/Well_Known_Text
 * @property {import('./SimpleLabel').default} [simpleLabel] Marker Simple Label object.
 * @property {import('./util').GeometryGeoJSON} [geometry] GeoJSON Geometry. Mandatory to use this property or `wkt` property. More info: https://geojson.org/ (From `geometry` property).
 * @property {number} [zIndex] Z-Index. Default 0.
 * @property {Object.<string, string>} extraProperties Extra properties to put inside geojson. Default {}.
 */

/**
 * @classdesc
 *
 * <b>Javascript ES5</b>
 * ```js
 * const feature = new cercaliagl.Feature({ ... });
 * ```
 *
 * A vector object for geographics features with a geometry and other attribute. Feature must need a geometry in GeoJSON format or in WKT format.
 * Then there are many attributes to adjust this feature styles and events.<br/>
 * These events can defined in constructor or in runtime modifing attribute as `onClick`, `onMouseOver`, etc.<br/>
 *
 * Example usage:
 *
 * ```js
 * const feature = new cercaliagl.Feature({
 *   wkt: 'POLYGON((2.177722861 41.413683777, 2.174677572 41.411310103, 2.171263974 41.413372702, 2.174830286 41.416057364, 2.177722861 41.413683777))',
 *   strokeColor: '#550000',
 *   strokeWidth: 2,
 *   fillOpacity: 0.5,
 *   simpleLabel: new cercaliagl.SimpleLabel({text: 'Example polygon'}),
 *   onClick: () => alert('Polygon click'),
 * });
 * map.addFeature(feature);
 * map.centerToFeatures([feature]);
 * ```
 *
 * @extends {BaseFeature}
 */
class Feature extends BaseFeature {
  /**
   * @constructor
   * @param {FeatureOptions} options
   * @api
   */
  constructor(options = {}) {
    super({
      id: options.id,
      onClick: options.onClick,
      onDoubleClick: options.onDoubleClick,
      onMouseOut: options.onMouseOut,
      onMouseOver: options.onMouseOver,
      onMouseMove: options.onMouseMove,
      onRightClick: options.onRightClick,
    });

    /**
     * @private
     * @type {import("./Map").default}
     */
    this.map_ = null;

    /**
     * @private
     * @type {boolean}
     */
    this.editable_ = options.editable !== undefined ? options.editable : false;

    /**
     * @private
     * @type {boolean}
     */
    this.showDirection_ = options.showDirection !== undefined ? options.showDirection : false;

    /**
     * @private
     * @type {boolean}
     */
    this.visible_ = options.visible !== undefined ? options.visible : true;

    /**
     * @private
     * @type {string}
     */
    this.fillColor_ = options.fillColor ? options.fillColor : '#009BFF';

    /**
     * @private
     * @type {string}
     */
    this.fillOpacity_ = options.fillOpacity ? options.fillOpacity : 0.2;

    /**
     * @private
     * @type {string}
     */
    this.strokeColor_ = options.strokeColor ? options.strokeColor : '#2196F3';

    /**
     * @private
     * @type {number}
     */
    this.strokeOpacity_ = options.strokeOpacity !== undefined ? options.strokeOpacity : 1;

    /**
     * @private
     * @type {number}
     */
    this.height_ = options.height ? options.height : 0;

    /**
     * @private
     * @type {number}
     */
    this.strokeWidth_ = options.strokeWidth !== undefined ? options.strokeWidth : 7;

    /**
     * @private
     * @type {boolean}
     */
    this.outline_ = options.outline !== undefined ? options.outline : true;

    /**
     * @private
     * @type {string}
     */
    this.outlineColor_ = options.outlineColor ? options.outlineColor : '#040252';

    /**
     * @private
     * @type {number}
     */
    this.outlineOpacity_ = options.outlineOpacity !== undefined ? options.outlineOpacity : 1;

    /**
     * @private
     * @type {number}
     */
    this.outlineWidth_ = options.outlineWidth !== undefined ? options.outlineWidth : 9;

    /**
     * @private
     * @type {string}
     */
    this.wkt_ = options.wkt || (options.geometry ? WKT.stringify(options.geometry) : null);

    /**
     * @private
     * @type {import("./util").GeometryGeoJSON}
     */
    this.geometry_ = this.wkt_ ? WKT.parse(this.wkt_) : options.geometry;

    /**
     * @private
     * @type {number}
     */
    this.zIndex_ = options.zIndex !== undefined ? options.zIndex : 0;

    /**
     * @private
     * @type {string}
     */
    this.encodedLineString_ = options.encodedLineString ? options.encodedLineString : null;

    /**
     * @private
     * @type {string}
     */
    this.encodedPolygon_ = options.encodedPolygon ? options.encodedPolygon : null;

    /**
     * @private
     * @type {Array.<string>}
     */
    this.encodedMultiPolygon_ = options.encodedMultiPolygon ? options.encodedMultiPolygon : null;

    /**
     * @private
     * @type {Array.<string>}
     */
    this.encodedGoogleMultiPolygon_ = options.encodedGoogleMultiPolygon ? options.encodedGoogleMultiPolygon : null;

    /**
     * @private
     * @type {string}
     */
    this.encodedGoogleLineString_ = options.encodedGoogleLineString ? options.encodedGoogleLineString : null;

    /**
     * @private
     * @type {string}
     */
    this.encodedGooglePolygon_ = options.encodedGooglePolygon ? options.encodedGooglePolygon : null;

    /**
     * @private
     * @type {number}
     */
    this.encodedPrecision_ = options.encodedPrecision ? options.encodedPrecision : 5;

    /**
     * @private
     * @type {import('./SimpleLabel').default|null}
     */
    this.simpleLabel_ = options.simpleLabel ? options.simpleLabel : null;

    /**
     * @type {function(this:Feature) : void|null}
     */
    this.onModifyStart = options.onModifyStart ? options.onModifyStart : null;

    /**
     * @type {function(this:Feature) : void|null}
     */
    this.onModifyEnd = options.onModifyEnd ? options.onModifyEnd : null;

    /**
     * @private
     * @type {Object.<string, string>}
     */
    this.extraProperties_ = options.extraProperties ? options.extraProperties : {};

    this.initialize_();
  }

  /**
   * @private
   */
  initialize_() {
    if (!this.wkt_ && !this.geometry_) {
      let wkt;
      if (this.encodedLineString_) {
        wkt = this.encodedDataToLinestringWKT_(this.encodedLineString_, true);
      } else if (this.encodedPolygon_) {
        wkt = this.encodedDataToPolygonWKT_(this.encodedPolygon_, true);
      } else if (this.encodedMultiPolygon_) {
        wkt = this.encodedDataToMultiPolygonWKT_(this.encodedMultiPolygon_, true);
      } else if (this.encodedGoogleLineString_) {
        wkt = this.encodedDataToLinestringWKT_(this.encodedGoogleLineString_);
      } else if (this.encodedGooglePolygon_) {
        wkt = this.encodedDataToPolygonWKT_(this.encodedGooglePolygon_);
      } else if (this.encodedGoogleMultiPolygon_) {
        wkt = this.encodedDataToMultiPolygonWKT_(this.encodedGoogleMultiPolygon_);
      }
      if (wkt) {
        this.wkt_ = wkt;
        this.geometry_ = WKT.parse(this.wkt_);
      }
    }

    if (this.geometry_) {
      this.handleChange_ = this.handleChange_.bind(this);
      this.updateGeoJSON_();

      if (this.simpleLabel_) {
        this.simpleLabel_.setBaseFeature(this);
        this.simpleLabel_.on('change', this.handleChange_);
      }
    } else {
      console.warn(`Feature ${this.id_} without geometry.`); // eslint-disable-line no-console
    }
  }

  /**
   * @private
   */
  handleChange_() {
    this.updateGeoJSON_();
    if (this.layer_) {
      this.layer_.updateFeatureGeoJSON(this);
      this.layer_.refreshGeoJSON();
    }
  }

  /**
   * @private
   * @return {Object} GeoJSON
   */
  updateGeoJSON_() {
    let simpleLabelProps = {};
    if (this.simpleLabel_) {
      simpleLabelProps = {
        textFont: ['Open Sans Regular'],
        textSize: this.simpleLabel_.getFontSize(),
        textPadding: 20,
        textAnchor: 'left',
        textJustify: 'left',
        textOffset: this.simpleLabel_.getOffset(),
        textField: this.simpleLabel_.getText(),
        textColor: this.simpleLabel_.getTextColor(),
        textRotation: this.simpleLabel_.getTextRotation(),
        textStrokeColor: this.simpleLabel_.getStrokeColor(),
        textStrokeWidth: this.simpleLabel_.getStrokeWidth(),
        textOpacity: this.simpleLabel_.getTextOpacity(),
      };
    }

    this.geojson_ = {
      type: 'Feature',
      properties: {
        id: `${this.getId()}`,
        height: this.height_,
        fillColor: this.fillColor_,
        fillOpacity: this.fillOpacity_,
        strokeColor: this.strokeColor_,
        strokeOpacity: this.strokeOpacity_,
        strokeWidth: this.strokeWidth_,
        outline: this.outline_,
        outlineColor: this.outlineColor_,
        outlineOpacity: this.outlineOpacity_,
        outlineWidth: this.outlineWidth_,
        visible: this.visible_,
        showDirection: this.showDirection_,
        zIndex: this.zIndex_,
        ...simpleLabelProps,
        ...this.extraProperties_,
      },
      geometry: this.geometry_,
    };
  }

  /**
   * Get feature bounds.
   * @api
   * @return {Bounds}
   */
  getBounds() {
    const bounds = new Bounds();
    if (this.geojson_.geometry.type === GeometryType.LINESTRING) {
      this.geometry_.coordinates.forEach((coordinate) => {
        bounds.extendXY(coordinate[0], coordinate[1]);
      });
    } else if (this.geojson_.geometry.type === GeometryType.MULTIPOLYGON) {
      this.geojson_.geometry.coordinates.forEach((coordinates) => {
        bounds.extend(Feature.getBoundsByPolygon(coordinates));
      });
    } else {
      bounds.extend(Feature.getBoundsByPolygon(this.geojson_.geometry.coordinates));
    }

    return bounds;
  }

  /**
   * Get closest feature coordinate giving a position.
   * @api
   * @param {LonLat} position Position
   * @return {LonLat} Closest coordinate to point
   */
  getClosestCoordinate(position) {
    return getClosestLonLatOfFeature(position, this);
  }

  /**
   * Get feature Geometry
   * @api
   * @return {import("./util").GeometryGeoJSON} Geometry
   */
  getGeometry() {
    return this.geometry_;
  }

  /**
   * Set geometry and update it.
   * @api
   * @param {import("./util").GeometryGeoJSON} geometry Geometry
   */
  setGeometry(geometry) {
    this.geometry_ = geometry;
    this.wkt_ = WKT.stringify(geometry);
    this.handleChange_();
  }

  getZIndex() {
    return this.zIndex_;
  }

  /**
   * Set zIndex and update it.
   * @api
   * @param {number} zIndex zIndex
   */
  setZIndex(zIndex) {
    this.zIndex_ = zIndex;
  }

  /**
   * Get geometry in WKT format.
   * @api
   * @return {string} Geometry in WKT format
   */
  getWKT() {
    return this.wkt_;
  }

  /**
   * Set geometry in WKT format and update it on map.
   * @api
   * @param {import("./util").GeometryGeoJSON} wkt WKT geometry
   */
  setWKT(wkt) {
    this.wkt_ = wkt;
    this.geometry_ = WKT.parse(wkt);
    this.handleChange_();
  }

  /**
   * Get simple label object. Returns `nullp  if it doesn't exist.
   * @api
   * @return {import('./SimpleLabel').default} Simple label
   */
  getSimpleLabel() {
    return this.simpleLabel_;
  }

  /**
   * Set simple label and update it on map.
   * @api
   * @param {import('./SimpleLabel').default|null} simpleLabel simple Label
   */
  setSimpleLabel(simpleLabel) {
    if (simpleLabel) {
      this.simpleLabel_ = simpleLabel;
      this.simpleLabel_.setBaseFeature(this);
      this.simpleLabel_.on('change', this.handleChange_);
    } else {
      this.simpleLabel_.off('change', this.handleChange_);
      this.simpleLabel_ = null;
    }
    this.handleChange_();
  }

  /**
   * Obtain if feature is editable.
   * @api
   * @return {boolean} is editable
   */
  isEditable() {
    return this.editable_;
  }

  /**
   * Set editable feature
   * @api
   * @param {boolean} editable `true` to become editable feature.
   */
  setEditable(editable) {
    this.editable_ = editable;
  }

  /**
   * Obtain if feature is showing direction.
   * @api
   * @return {boolean} is showing direction
   */
  isShowingDirection() {
    return this.showDirection_;
  }

  /**
   * Set show direction feature
   * @api
   * @param {boolean} showDirection `true` to show feature's direction.
   */
  setShowDirection(showDirection) {
    this.showDirection_ = showDirection;
    this.handleChange_();
  }

  /**
   * Enable edit mode if `editable` is true. Use `setEditable` to change `editable` attribute.
   * @param {boolean} enable
   */
  editMode(enable) {
    if (this.editable_) {
      if (this.map_) {
        const draw = this.map_.getControlByName(MapControls.DRAW);
        if (enable) {
          draw.select(this);
          this.setVisible(false);
        } else {
          draw.unselect(this);
          this.setVisible(true);
        }
      }
    }
  }

  /**
   * Obtain if feature is visible
   * @api
   * @return {boolean} Return `true` if feature is visible
   */
  isVisible() {
    return this.visible_;
  }

  /**
   * Show or hide feature.
   * @api
   * @param {boolean} visible Visible `true`, not visible `false`.
   */
  setVisible(visible) {
    this.visible_ = visible;
    this.handleChange_();
  }

  /**
   * Change feature fill color
   * @api
   * @param {string} color Color in hexadecimal format. For example: `#ff0000`.
   */
  setFillColor(color) {
    this.fillColor_ = color;
    this.handleChange_();
  }

  /**
   * @api
   * @param {*} color Return color in hexadecimal format. For example `#ff0000`
   */
  getFillColor() {
    return this.fillColor_;
  }

  /**
   * Fill opacity.
   * @api
   * @param {number} opacity Values between `0` and `1`.
   */
  setFillOpacity(opacity) {
    this.fillOpacity_ = opacity;
    this.handleChange_();
  }

  /**
   * Get fill opacity
   * @api
   * @return {number} Values between `0` and `1`.
   */
  getFillOpacity() {
    return this.fillOpacity_;
  }

  /**
   * Stroke opacity.
   * @api
   * @param {number} opacity Values between `0` and `1`.
   */
  setStrokeOpacity(opacity) {
    this.strokeOpacity_ = opacity;
    this.handleChange_();
  }

  /**
   * Get stroke opacity
   * @api
   * @return {number} Values between `0` and `1`.
   */
  getStrokeOpacity() {
    return this.strokeOpacity_;
  }

  /**
   * Change feature stroke color
   * @api
   * @param {string} color Color in hexadecimal format. For example: `#ff0000`.
   */
  setStrokeColor(color) {
    this.strokeColor_ = color;
    this.handleChange_();
  }

  /**
   * @api
   * @return {string} Return color in hexadecimal format. For example `#ff0000`
   */
  getStrokeColor() {
    return this.strokeColor_;
  }

  /**
   * Change feature stroke width
   * @api
   * @param {number} width Width.
   */
  setStrokeWidth(width) {
    this.strokeWidth_ = width;
    this.handleChange_();
  }

  /**
   * @api
   * @return {number} Width.
   */
  getStrokeWidth() {
    return this.strokeWidth_;
  }

  /**
   * Change feature outline color
   * @api
   * @param {string} color Color in hexadecimal format. For example: `#ff0000`.
   */
  setOutlineColor(color) {
    this.outlineColor_ = color;
    this.handleChange_();
  }

  /**
   * @api
   * @return {string} Return color in hexadecimal format. For example `#ff0000`
   */
  getOutlineColor() {
    return this.outlineColor_;
  }

  /**
   * Change feature outline width
   * @api
   * @param {number} width Width.
   */
  setOutlineWidth(width) {
    this.outlineWidth_ = width;
    this.handleChange_();
  }

  /**
   * Get outline width
   * @api
   * @return {number} Width.
   */
  getOutlineWidth() {
    return this.outlineWidth_;
  }

  /**
   * Change outline opacity
   * @api
   * @param {number} opacity Opacity. Values between `0` and `1`.
   */
  setOutlineOpacity(opacity) {
    this.outlineOpacity_ = opacity;
    this.handleChange_();
  }

  /**
   * Get opacity
   * @api
   * @return {number} Opacity. Values between `0` and `1`.
   */
  getOutlineOpacity() {
    return this.outlineOpacity_;
  }

  /**
   * @static
   * @param {Array.<number>} polygonsCoordinates Polygon coordinates
   * @return {Bounds}
   */
  static getBoundsByPolygon(polygonsCoordinates) {
    const bounds = new Bounds();
    polygonsCoordinates.forEach((coordinates) => {
      coordinates.forEach((coordinate) => {
        bounds.extendXY(coordinate[0], coordinate[1]);
      });
    });
    return bounds;
  }

  /**
   * @static
   * @param {Object} geojsonFeature GeoJSON feature
   * @param {FeatureOptions} [featureOptions] Feature options
   * @api
   */
  static fromGeoJSON(geojsonFeature, featureOptions = {}) {
    return new Feature({
      id: geojsonFeature.id,
      geometry: geojsonFeature.geometry,
      ...featureOptions,
    });
  }

  /**
   * Get extra properties
   * @return {Object.<string, string>} Extra properties
   * @api
   */
  getExtraProperties() {
    return this.extraProperties_;
  }

  /**
       * Set extra properties
       * @param {Object.<string, string>} properties Extra properties
       * @api
       */
  setExtraProperties(properties) {
    this.extraProperties_ = properties;
    this.handleChange_();
  }
}

export default Feature;
